import type { MouseEvent, ReactNode } from "react";
import { useT, type UiLang } from "../../lib/i18n";
import { LangSwitch } from "./LangSwitch";

/**
 * 登录页与「先绑定 Discord」页共用的外框：浅色底 + 居中卡片，语言切换放右上角。
 * 两页都在没进系统之前出现，长得一样，人才知道自己还在同一个登录流程里。
 */
export function AuthLayout({
  uiLang, onUiLang, width = 420, children, footer,
}: { uiLang: UiLang; onUiLang: (l: UiLang) => void; width?: number; children: ReactNode; footer?: ReactNode }) {
  const t = useT();
  return (
    <div className="relative flex min-h-screen flex-col bg-page">
      {/* 两团很淡的主色光晕，只为让大片空白不那么像报错页；不承载任何信息 */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-40 -top-40 h-[480px] w-[480px] rounded-full bg-primary-100 opacity-60 blur-3xl" />
        <div className="absolute -bottom-48 -right-32 h-[520px] w-[520px] rounded-full bg-[#5865F2] opacity-[0.08] blur-3xl" />
      </div>

      <header className="relative flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-white shadow-light">
            {/* 纸飞机：飞行棋的「飞」，不用骰子是因为小尺寸下点数糊成一团 */}
            <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor" aria-hidden>
              <path d="M2.4 11.1 20.6 3.3c.8-.3 1.5.4 1.2 1.2l-7.8 18.2c-.3.8-1.5.8-1.8 0l-2.4-6.6-6.6-2.4c-.8-.3-.8-1.5 0-1.8Zm7.9 4.1 1.6 4.4 5.6-13-8.6 7.2 1.4 1.4Z" />
            </svg>
          </span>
          <span className="text-base font-semibold text-ink">{t("app.title")}</span>
        </div>
        <LangSwitch value={uiLang} onChange={onUiLang} />
      </header>

      <main className="relative flex flex-1 items-center justify-center px-4 pb-16">
        <div className="w-full" style={{ maxWidth: width }}>
          <div className="rounded-xl border border-line-lighter bg-white px-8 py-9 shadow-base">{children}</div>
          {footer && <div className="mt-5 px-2 text-center text-xs leading-relaxed text-ink-placeholder">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

/** Discord 官方标识（simple-icons 的 Clyde 轮廓），只用在「用 Discord 登录」按钮上 */
export function DiscordMark({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.1.246.198.373.292a.077.077 0 0 1-.007.128 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.363 1.225 1.993a.076.076 0 0 0 .084.029 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.029ZM8.02 15.331c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.332-.956 2.418-2.157 2.418Zm7.975 0c-1.183 0-2.157-1.086-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.332-.946 2.418-2.157 2.418Z" />
    </svg>
  );
}

/**
 * 「用 Discord 登录」主按钮。是 `<a>` 不是 `<button>`：登录页拦下普通点击改开小窗，
 * 中键 / 右键 / 小窗被拦时仍是整页跳到 notify-hub；busy 时换成不可点的 span，避免换票途中又跳走。
 */
export function DiscordButton({ href, onClick, busy, children }: { href: string; onClick?: (e: MouseEvent<HTMLAnchorElement>) => void; busy?: boolean; children: ReactNode }) {
  const cls = "flex h-11 w-full items-center justify-center gap-2.5 rounded-lg text-[15px] font-medium text-white transition-colors";
  if (busy) return <span className={`${cls} cursor-not-allowed bg-[#5865F2] opacity-60`}><DiscordMark />{children}</span>;
  return (
    <a href={href} onClick={onClick} className={`${cls} bg-[#5865F2] shadow-light hover:bg-[#4752C4] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5865F2]`}>
      <DiscordMark />{children}
    </a>
  );
}

export function Spinner({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={`animate-spin ${className}`} fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity=".2" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
